/**
 * <TransactionFormShell /> — the layout every HR-initiate form
 * inherits (M30.5.3).
 *
 * Page layout (desktop): two-column. Left column has the form
 * fields + submit row. Right column has TransactionContextPanel
 * (employee snapshot) + 5 AIAssistPanels stacked. Mobile stacks
 * vertically.
 *
 * The shell is generic over the body type — the per-form page
 * supplies the body fields + the assist/commit callers. The shell
 * handles:
 *   - Calling /assist via useTransactionAssist as form values
 *     change (debounced)
 *   - Rendering the 5 AI panels with onAcknowledge wired
 *   - Disabling submit when blocking errors are unacknowledged
 *   - Showing a success/error banner after commit
 *
 * Per-form pages call into this with a children render-prop that
 * returns the type-specific form fields.
 */

import { CheckCircle, XCircle } from "lucide-react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { LtrAmount, LtrText, PageHeader } from "../ui";

import { AIAssistPanel } from "./AIAssistPanel";
import { TransactionContextPanel } from "./TransactionContextPanel";

import type {
  AssistPanelOut,
  TransactionAssistOut,
} from "../../lib/api";
import type {
  Panel,
  TransactionType,
} from "../../lib/ai-tier-registry";

export interface TransactionFormShellProps {
  transactionType: TransactionType;
  title: string;
  /** Result of the live /assist call. Null until enabled. */
  assist: TransactionAssistOut | null;
  isAssistLoading: boolean;
  assistError: string | null;
  /** Codes HR has acknowledged. */
  acknowledgedCodes: Set<string>;
  onAcknowledge: (code: string) => void;
  onAcceptSuggestion?: (fieldRef: string, value: unknown) => void;
  /** Submit handler — the per-form page wires the actual
   *  commit call here. */
  onSubmit: () => void | Promise<void>;
  /** True while the commit is in flight. */
  isSubmitting: boolean;
  /** Success state after a successful commit. */
  submitSuccess: boolean;
  /** Error message after a failed commit. */
  submitError: string | null;
  /** Per-form fields rendered in the left column. */
  children: ReactNode;
  /** Optional context-panel data — when provided renders the
   *  employee snapshot in the right column. */
  context?: import("./TransactionContextPanel").TransactionContextSnapshot;
}

const PANEL_ORDER: Panel[] = [
  "validation",
  "cross_reference",
  "suggestions",
  "completeness",
  "auto_fill",
];

export function TransactionFormShell({
  transactionType,
  title,
  assist,
  isAssistLoading,
  assistError,
  acknowledgedCodes,
  onAcknowledge,
  onAcceptSuggestion,
  onSubmit,
  isSubmitting,
  submitSuccess,
  submitError,
  children,
  context,
}: TransactionFormShellProps) {
  const { t } = useTranslation();
  const blocked = !!assist?.has_blocking_errors &&
    assist.panels.some((p: AssistPanelOut) =>
      p.findings.some(
        (f) =>
          f.severity === "error" && !acknowledgedCodes.has(f.code),
      ),
    );
  const previewItems = assist?.preview_payroll_entries ?? [];

  return (
    <div className="mx-auto flex max-w-workspace flex-col gap-6 p-6">
      <PageHeader title={title} />

      {submitSuccess && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border border-state-approved/40 bg-state-approved-bg/60 p-3 text-sm text-state-approved"
        >
          <CheckCircle className="h-4 w-4" />
          {t("operations.form_submit_success")}
        </div>
      )}
      {submitError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-state-denied/40 bg-state-denied-bg/60 p-3 text-sm text-state-denied"
        >
          <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{submitError}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        {/* Form column */}
        <section className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-surface-raised p-5 shadow-sm">
          {children}

          {previewItems.length > 0 && (
            <div className="rounded-md border border-border-subtle/40 bg-surface-sunken p-3">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Payroll preview
              </h3>
              <ul className="flex flex-col gap-1 text-sm">
                {previewItems.map((pli, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-2"
                  >
                    <LtrText className="text-text-secondary">
                      {pli.line_item_type}
                    </LtrText>
                    <span className="font-medium text-text-primary">
                      <LtrAmount
                        value={Number(pli.amount)}
                        currency={pli.currency}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-border-subtle/40 pt-4">
            {blocked && (
              <p className="text-xs text-state-denied">
                {t("operations.form_submit_blocked")}
              </p>
            )}
            <button
              type="button"
              onClick={() => void onSubmit()}
              disabled={blocked || isSubmitting}
              className="inline-flex items-center justify-center rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("operations.form_submit")}
            </button>
          </div>
        </section>

        {/* Sidebar: context + AI panels */}
        <aside className="flex flex-col gap-3">
          {context && <TransactionContextPanel context={context} />}

          {isAssistLoading && !assist && (
            <p className="text-xs text-text-secondary">
              {t("operations.form_loading_assist")}
            </p>
          )}
          {!assist && !isAssistLoading && (
            <div className="flex flex-col gap-2">
              <p className="rounded-md border border-border-subtle bg-surface-sunken p-3 text-xs text-text-secondary">
                {t("operations.form_pick_employee_to_start")}
              </p>
              <p className="px-1 text-[11px] uppercase tracking-wider text-text-tertiary">
                {t(
                  "operations.form_panels_preview_label",
                  "AI will review across 5 dimensions:",
                )}
              </p>
              {PANEL_ORDER.map((p) => (
                <div
                  key={`preview-${p}`}
                  className="rounded-md border border-dashed border-border-subtle bg-surface-default px-3 py-2 opacity-60"
                >
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
                    {t(`ai_panel.${p}_label`, p)}
                  </div>
                  <div className="mt-0.5 text-xs text-text-secondary">
                    {t(`ai_panel.${p}_preview_hint`, "")}
                  </div>
                </div>
              ))}
            </div>
          )}
          {assistError && (
            <p className="rounded-md border border-state-denied/40 bg-state-denied-bg/40 p-3 text-xs text-state-denied">
              {assistError}
            </p>
          )}

          {assist &&
            PANEL_ORDER.map((p) => {
              const panel = assist.panels.find((x) => x.panel === p);
              if (!panel) return null;
              return (
                <AIAssistPanel
                  key={p}
                  transactionType={transactionType}
                  panel={{
                    panel: p,
                    findings: panel.findings.map((f) => ({
                      panel: p,
                      severity: f.severity,
                      code: f.code,
                      message: f.message,
                      field_ref: f.field_ref,
                      suggested_value: f.suggested_value,
                      citation: f.citation,
                    })),
                  }}
                  acknowledgedCodes={acknowledgedCodes}
                  onAcknowledge={onAcknowledge}
                  onAcceptSuggestion={onAcceptSuggestion}
                />
              );
            })}
        </aside>
      </div>
    </div>
  );
}
